import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Heart, Users, Star, MessageSquare, Filter, Search, ArrowUp, 
  ArrowDown, Clock, Check, X, ArrowRight, Download, ChevronDown,
  ThumbsUp, Lightbulb, Zap, Trophy, Bell
} from 'lucide-react';
import confetti from 'canvas-confetti';

// react-snap prerenders every route in a headless browser with the user
// agent "ReactSnap". The leaderboard API is a different origin and is
// CORS-blocked from the crawler's localhost origin, so fetching it during the
// prerender fails (net::ERR_FAILED). Detect the prerender, skip the request,
// and render this committed fallback set so the static HTML ships real content
// instead of an error panel.
const isPrerender = () =>
  typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

const FALLBACK_FEATURES = [
  { 
    id: "1", 
    title: "Duplicate file detection", // Using title field to match API format
    name: "Duplicate file detection", // Include name for backwards compatibility
    description: "Identify and handle duplicate files during sorting", 
    normalized_score: 0.85, 
    tier: "high_priority",
    status: "planned",
    is_trending: true
  },
  { 
    id: "2", 
    title: "Custom sorting rules editor",
    name: "Custom sorting rules editor", 
    description: "Create and save your own sorting rules and templates", 
    normalized_score: 0.72, 
    tier: "high_priority",
    status: "in_progress",
    is_trending: false
  },
  { 
    id: "3", 
    title: "Folder synchronization",
    name: "Folder synchronization", 
    description: "Keep folders in sync with your organization rules", 
    normalized_score: 0.65, 
    tier: "medium_priority",
    status: "proposed",
    is_trending: false
  },
  { 
    id: "4", 
    title: "Dark mode interface",
    name: "Dark mode interface", 
    description: "Switch between light and dark mode themes", 
    normalized_score: 0.61, 
    tier: "medium_priority",
    status: "completed",
    is_trending: false,
    isNew: true
  },
  { 
    id: "5", 
    title: "File preview integration",
    name: "File preview integration", 
    description: "Preview files before and after sorting", 
    normalized_score: 0.53, 
    tier: "medium_priority",
    status: "proposed",
    is_trending: true
  },
  { 
    id: "6", 
    title: "Rule sharing marketplace",
    name: "Rule sharing marketplace", 
    description: "Share and discover organization rules from the community", 
    normalized_score: 0.48, 
    tier: "low_priority",
    status: "planned",
    is_trending: false
  },
  { 
    id: "7", 
    title: "Context menu integration",
    name: "Context menu integration", 
    description: "Right click to access Sortio from the file explorer", 
    normalized_score: 0.42, 
    tier: "low_priority",
    status: "completed",
    is_trending: false
  },
  { 
    id: "8", 
    title: "Keyboard shortcuts",
    name: "Keyboard shortcuts", 
    description: "Customize keyboard shortcuts for faster workflow", 
    normalized_score: 0.38, 
    tier: "low_priority",
    status: "planned",
    is_trending: false
  },
  { 
    id: "9", 
    title: "Folder watch mode",
    name: "Folder watch mode", 
    description: "Automatically organize new files as they arrive in folders", 
    normalized_score: 0.33, 
    tier: "consideration",
    status: "in_progress",
    is_trending: true
  },
  { 
    id: "10", 
    title: "Advanced file renaming",
    name: "Advanced file renaming", 
    description: "Rename files based on patterns and content", 
    normalized_score: 0.29, 
    tier: "consideration",
    status: "proposed",
    is_trending: false
  },
  {
    id: "12",
    title: "Cloud storage integration",
    name: "Cloud storage integration", 
    description: "Connect to Dropbox, Google Drive, and OneDrive", 
    normalized_score: 0.22, 
    tier: "consideration",
    status: "planned",
    is_trending: false
  },
];

const buildCommunityStats = (list) => ({
  // normalized_score is a 0-1 value; show summed popularity as a percentage.
  totalVotes: Math.round(
    list.reduce((sum, feature) => sum + (feature.normalized_score || 0), 0) * 100
  ),
  featuresImplemented: list.filter((f) => f.status === 'completed').length,
  pendingFeatures: list.filter((f) => f.status !== 'completed').length,
  totalCommunityMembers: 2400,
});

const FeatureRequestCard = ({ feature, onVote, hasVoted }) => {
  // Status styling with Apple's aesthetic
  const statusColors = {
    "proposed": "bg-gray-200/10 text-gray-300 border-gray-300/20",
    "planned": "bg-sortio-primary/10 text-sortio-primary border-sortio-primary/20",
    "in_progress": "bg-blue-500/10 text-blue-400 border-blue-500/20",
    "completed": "bg-sortio-secondary/10 text-sortio-secondary border-sortio-secondary/20"
  };

  // Tier styling
  const tierColors = {
    "high_priority": "bg-red-500/10 text-red-400 border-red-500/20",
    "medium_priority": "bg-amber-500/10 text-amber-400 border-amber-500/20",
    "low_priority": "bg-blue-500/10 text-blue-400 border-blue-500/20",
    "consideration": "bg-purple-500/10 text-purple-400 border-purple-500/20"
  };

  const progressMap = {
    "proposed": 0,
    "planned": 33,
    "in_progress": 66,
    "completed": 100
  };

  // Get score percentage from normalized_score (0-1 value)
  const getScorePercentage = () => {
    return Math.round((feature.normalized_score || 0) * 100);
  };

  const cardRef = useRef(null);

  const handleVote = () => {
    if (!hasVoted) {
      onVote(feature.id);
      
      // Trigger confetti on the card
      const rect = cardRef.current.getBoundingClientRect();
      const x = rect.left + rect.width / 2;
      const y = rect.top + rect.height / 2;
      
      confetti({
        particleCount: 50,
        spread: 70,
        origin: { 
          x: x / window.innerWidth,
          y: y / window.innerHeight
        }
      });
    }
  };

  return (
    <div 
      ref={cardRef} 
      className="group backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-6 rounded-2xl mb-5 hover:border-sortio-primary/30 hover:bg-apple-700/40 transition-all duration-300 relative overflow-hidden"
    >
      {/* Subtle gradient light effect on hover */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none bg-gradient-to-r from-apple-700/0 via-sortio-primary/5 to-apple-700/0"></div>
      
      <div className="flex justify-between items-start gap-4">
        <div className="flex-grow">
          <h3 className="text-xl font-medium text-apple-50 mb-2">{feature.name}</h3>
          <p className="text-sm text-apple-300 mb-4 leading-relaxed">{feature.description}</p>
          
          {feature.status !== "proposed" && (
            <div className="mb-4">
              <div className="flex justify-between text-xs mb-1">
                <span className="text-apple-300 font-medium">Implementation Progress</span>
                <span className="text-apple-200">{progressMap[feature.status]}%</span>
              </div>
              <div className="w-full bg-apple-800/50 rounded-full h-1.5 overflow-hidden">
                <div 
                  className={`h-1.5 rounded-full ${
                    feature.status === "completed" 
                      ? "bg-gradient-to-r from-sortio-secondary to-sortio-secondary/80" 
                      : "bg-gradient-to-r from-sortio-primary to-sortio-primary/80"
                  }`}
                  style={{ width: `${progressMap[feature.status]}%` }}
                ></div>
              </div>
            </div>
          )}
          
          <div className="flex flex-wrap gap-2">
            {/* Status badge */}
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusColors[feature.status]} border backdrop-blur-sm`}>
              {feature.status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </div>
            
            {/* Priority tier badge */}
            {feature.tier && (
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${tierColors[feature.tier]} border backdrop-blur-sm`}>
                {feature.tier.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
              </div>
            )}
            
            {/* Completed badge */}
            {feature.status === "completed" && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-sortio-secondary/10 text-sortio-secondary border border-sortio-secondary/20 backdrop-blur-sm">
                <Check size={12} className="mr-1" />
                Now Available
              </div>
            )}
            
            {/* Trending badge */}
            {feature.is_trending && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 backdrop-blur-sm">
                <Zap size={12} className="mr-1" />
                Trending
              </div>
            )}
            
            {/* New badge */}
            {feature.isNew && (
              <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-500/10 text-blue-400 border border-blue-500/20 backdrop-blur-sm">
                New
              </div>
            )}
          </div>
        </div>
        
        <div className="flex flex-col items-center">
          <button 
            onClick={handleVote}
            disabled={hasVoted}
            className={`p-3 rounded-full shadow-sm ${hasVoted 
              ? 'bg-sortio-primary/20 text-sortio-primary cursor-default' 
              : 'bg-apple-800/50 border border-apple-600/20 text-apple-300 hover:bg-sortio-primary/20 hover:text-sortio-primary hover:border-sortio-primary/30 transition-all'}`}
            aria-label="Vote for this feature"
          >
            <Heart size={20} className={hasVoted ? 'fill-sortio-primary' : ''} />
          </button>
          {/* Display popularity score as percentage instead of raw vote count */}
          <span className="text-lg font-medium mt-2 text-apple-50 group-hover:text-white transition-colors">{getScorePercentage()}%</span>
          <span className="text-xs text-apple-400">popularity</span>
        </div>
      </div>
      
      {feature.status === "completed" && (
        <div className="mt-4 pt-4 border-t border-apple-700/20">
          <a 
            href="/download" 
            className="flex items-center text-sm text-sortio-secondary hover:underline group-hover:brightness-110 transition-all"
          >
            <Download size={14} className="mr-1" />
            Try this feature in Sortio
          </a>
        </div>
      )}
    </div>
  );
};

const SuggestFeatureForm = ({ onSubmit }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const formRef = useRef(null);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!title || !description || description.length < 10) return;
    
    setIsSubmitting(true);
    
    try {
      // Here you would normally submit to an API
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      onSubmit({
        title,
        description,
        category
      });
      
      // Clear form
      setTitle('');
      setDescription('');
      setCategory('');
      
      // Trigger confetti
      const rect = formRef.current.getBoundingClientRect();
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { 
          x: 0.5,
          y: rect.bottom / window.innerHeight 
        }
      });
      
    } catch (error) {
      console.error('Error submitting feature:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div ref={formRef} className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-6 rounded-2xl relative overflow-hidden">
      {/* Subtle decorative element */}
      <div className="absolute -top-24 -right-24 w-48 h-48 bg-sortio-primary/5 rounded-full blur-3xl"></div>
      
      <div className="relative z-10">
        <h3 className="text-xl font-medium text-apple-50 mb-6 flex items-center">
          <div className="w-8 h-8 rounded-full bg-sortio-primary/10 flex items-center justify-center mr-3">
            <Lightbulb size={16} className="text-sortio-primary" />
          </div>
          Suggest a New Feature
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-5">
            <label htmlFor="title" className="block text-sm font-medium text-apple-200 mb-1.5">
              Feature Title *
            </label>
            <input
              type="text"
              id="title"
              className="w-full px-4 py-3 bg-apple-800/50 border border-apple-700/70 rounded-xl text-apple-100 focus:outline-none focus:ring-1 focus:ring-sortio-primary/70 focus:border-sortio-primary/30 transition-all duration-200 placeholder-apple-500"
              placeholder="What feature would you like to see?"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="mb-5">
            <label htmlFor="description" className="block text-sm font-medium text-apple-200 mb-1.5">
              Description * <span className="text-xs text-apple-400">(minimum 10 characters)</span>
            </label>
            <textarea
              id="description"
              rows="4"
              className={`w-full px-4 py-3 bg-apple-800/50 border ${description && description.length < 10 ? 'border-red-500/70' : 'border-apple-700/70'} rounded-xl text-apple-100 focus:outline-none focus:ring-1 focus:ring-sortio-primary/70 focus:border-sortio-primary/30 transition-all duration-200 placeholder-apple-500`}
              placeholder="Please describe how this feature would work and why it would be valuable..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            ></textarea>
            {description && description.length < 10 && (
              <p className="mt-1 text-xs text-red-400">Description must be at least 10 characters long.</p>
            )}
          </div>
          
          <div className="mb-6">
            <label htmlFor="category" className="block text-sm font-medium text-apple-200 mb-1.5">
              Category (Optional)
            </label>
            <div className="relative">
              <select
                id="category"
                className="w-full appearance-none px-4 py-3 bg-apple-800/50 border border-apple-700/70 rounded-xl text-apple-100 focus:outline-none focus:ring-1 focus:ring-sortio-primary/70 focus:border-sortio-primary/30 transition-all duration-200 pr-10"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select a category</option>
                <option value="ui">User Interface</option>
                <option value="sorting">Sorting Logic</option>
                <option value="integration">Integrations</option>
                <option value="other">Other</option>
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3">
                <ChevronDown size={16} className="text-apple-400" />
              </div>
            </div>
          </div>
          
          <div className="bg-apple-800/20 rounded-xl p-4 border border-apple-700/30 mb-6">
            <h4 className="font-medium text-apple-200 text-sm mb-2 flex items-center">
              <MessageSquare size={14} className="mr-1.5 text-sortio-primary" />
              Guidelines for Suggestions
            </h4>
            <ul className="text-sm text-apple-400 space-y-1.5 ml-1">
              <li className="flex items-start">
                <div className="text-sortio-primary mt-0.5 mr-2">•</div>
                <span>Be specific about what problem the feature would solve</span>
              </li>
              <li className="flex items-start">
                <div className="text-sortio-primary mt-0.5 mr-2">•</div>
                <span>Must be relevant to file organization and Sortio's core functionality</span>
              </li>
              <li className="flex items-start">
                <div className="text-sortio-primary mt-0.5 mr-2">•</div>
                <span>One feature per submission (no feature lists)</span>
              </li>
              <li className="flex items-start">
                <div className="text-sortio-primary mt-0.5 mr-2">•</div>
                <span>Inappropriate or unrelated suggestions will be automatically rejected</span>
              </li>
            </ul>
          </div>
          
          <button
            type="submit"
            disabled={isSubmitting || !title || !description || description.length < 10}
            className={`w-full py-3.5 rounded-full flex items-center justify-center font-medium transition-all ${
              isSubmitting || !title || !description || description.length < 10
                ? 'bg-apple-800/70 text-apple-400 cursor-not-allowed'
                : 'bg-sortio-primary text-white hover:bg-sortio-primary/90 shadow-sm hover:shadow'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/30 border-t-white mr-2"></div>
                Submitting...
              </>
            ) : (
              <>
                <Lightbulb size={18} className="mr-2" />
                Submit Feature Suggestion
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

const EmailCaptureForm = ({ onSubscribe, votedFeatureIds = [] }) => {
  const [email, setEmail] = useState('');
  const [subscribeAll, setSubscribeAll] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) return;

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('https://app.getsortio.com/api/feature-leaderboard/subscribe/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          subscribe_all: subscribeAll,
          feature_ids: subscribeAll ? [] : votedFeatureIds,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to subscribe');
      }

      onSubscribe({
        email,
        subscribeAll
      });

      // Clear form
      setEmail('');
      setSubscribeAll(false);

    } catch (error) {
      console.error('Error subscribing:', error);
      setError(error.message || 'Failed to subscribe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-6 rounded-2xl relative overflow-hidden">
      {/* Subtle decorative element */}
      <div className="absolute -top-24 -left-24 w-48 h-48 bg-sortio-secondary/5 rounded-full blur-3xl"></div>
      
      <div className="relative z-10">
        <h3 className="text-xl font-medium text-apple-50 mb-3 flex items-center">
          <div className="w-8 h-8 rounded-full bg-sortio-secondary/10 flex items-center justify-center mr-3">
            <Bell size={16} className="text-sortio-secondary" />
          </div>
          Stay Updated
        </h3>
        <p className="text-apple-300 text-sm mb-5 ml-11">
          Get notified when features you care about are implemented or updated.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-apple-200 mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              className="w-full px-4 py-3 bg-apple-800/50 border border-apple-700/70 rounded-xl text-apple-100 focus:outline-none focus:ring-1 focus:ring-sortio-secondary/70 focus:border-sortio-secondary/30 transition-all duration-200 placeholder-apple-500"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          
          <div className="flex items-center p-4 bg-apple-800/20 rounded-xl border border-apple-700/30">
            <div className="relative flex items-center h-5">
              <input
                id="subscribe-all"
                type="checkbox"
                className="h-4 w-4 text-sortio-secondary border-apple-700 rounded-sm focus:ring-sortio-secondary focus:ring-opacity-25 focus:ring-offset-0 bg-apple-800 transition duration-150"
                checked={subscribeAll}
                onChange={(e) => setSubscribeAll(e.target.checked)}
              />
              <div className={`absolute w-4 h-4 transition-opacity duration-200 pointer-events-none flex items-center justify-center ${subscribeAll ? 'opacity-100' : 'opacity-0'}`}>
                <Check size={10} className="text-white" />
              </div>
            </div>
            <label htmlFor="subscribe-all" className="ml-2 block text-sm text-apple-200">
              Subscribe to all feature updates
            </label>
          </div>
          
          <div className="text-xs text-apple-400 px-1">
            We'll only email you when features you voted for are implemented or when major updates happen.
            You can unsubscribe at any time. We never share your email with third parties.
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-900/20 border border-red-800/30 rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting || !email}
            className={`w-full py-3 rounded-full flex items-center justify-center font-medium transition-all duration-200 ${
              isSubmitting || !email
                ? 'bg-apple-800/70 text-apple-400 cursor-not-allowed'
                : 'bg-sortio-secondary text-black hover:bg-sortio-secondary/90 shadow-sm hover:shadow'
            }`}
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-black/30 border-t-black mr-2"></div>
                Subscribing...
              </>
            ) : (
              <>
                <Bell size={16} className="mr-2" />
                Subscribe for Updates
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};


const LeaderboardPage = () => {
  const [features, setFeatures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('votes');
  const [sortOrder, setSortOrder] = useState('desc');
  const [votedFeatures, setVotedFeatures] = useState([]);
  const [communityStats, setCommunityStats] = useState({
    totalVotes: 0,
    featuresImplemented: 0,
    pendingFeatures: 0,
    totalCommunityMembers: 0
  });
  
  // Initialize from localStorage
  useEffect(() => {
    const storedVotes = localStorage.getItem('voted_features');
    if (storedVotes) {
      setVotedFeatures(JSON.parse(storedVotes));
    }
  }, []);
  
  // Fetch features when sortBy, sortOrder, or statusFilter changes
  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        setIsLoading(true);
        // Skip the cross-origin API during the react-snap prerender (see the
        // isPrerender note above); render the committed fallback set so the
        // static page ships real content instead of a CORS error panel.
        if (isPrerender()) {
          setFeatures(FALLBACK_FEATURES);
          setCommunityStats(buildCommunityStats(FALLBACK_FEATURES));
          setIsLoading(false);
          return;
        }
        
        // Construct API URL with sorting and filtering parameters
        let apiUrl = 'https://app.getsortio.com/api/feature-leaderboard/';
        const params = new URLSearchParams();
        
        // Add sorting parameter if not using default
        if (sortBy !== 'votes') {
          // Map frontend sort values to API expected values
          const sortMapping = {
            votes: 'votes',
            name: 'name',
            status: 'status',
            newest: 'newest',
            updated: 'updated',
            trending: 'trending'
          };
          params.append('sort_by', sortMapping[sortBy] || 'votes');
        }
        
        // Add status filter if not showing all
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }
        
        // Append params to URL if any exist
        if (params.toString()) {
          apiUrl += `?${params.toString()}`;
        }
        
        // Fetch data from the API
        const response = await fetch(apiUrl);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Handle different API response formats
        let featuresArray = [];
        
        // Check if data is in a paginated format with "results" field (new API format)
        if (data.results && Array.isArray(data.results)) {
          featuresArray = data.results;
        }
        // Check if data is an array directly (old format)
        else if (Array.isArray(data)) {
          featuresArray = data;
        }
        // Check if features are nested in a different field
        else if (data.features && Array.isArray(data.features)) {
          featuresArray = data.features;
        }
        
        // Map API response fields to frontend expected fields if needed
        const mappedFeatures = featuresArray.map(feature => ({
          id: feature.id,
          name: feature.title || feature.name, // Handle both title and name fields
          description: feature.description,
          normalized_score: feature.normalized_score || 0,
          tier: feature.tier,
          status: feature.status,
          is_trending: feature.is_trending,
          updated_at: feature.updated_at,
          created_at: feature.created_at,
          isNew: new Date(feature.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) // Mark as new if created within the last 7 days
        }));
        
        setFeatures(mappedFeatures);
        
        // Calculate stats using normalized_score instead of vote count
        const totalPopularity = mappedFeatures.reduce((sum, feature) => sum + (feature.normalized_score || 0), 0);
        const implemented = mappedFeatures.filter(f => f.status === 'completed').length;
        const pending = mappedFeatures.filter(f => f.status !== 'completed').length;
        const trendingCount = mappedFeatures.filter(f => f.is_trending).length;
        
        setCommunityStats({
          totalVotes: Math.round(totalPopularity * 100), // Convert to percentage for display
          featuresImplemented: implemented,
          pendingFeatures: pending,
          totalCommunityMembers: 2400 // Placeholder
        });
        
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching feature leaderboard:', err);
        setError(err.message);
        setIsLoading(false);
        
        // Fall back to the committed feature set if the runtime API call fails.
        setFeatures(FALLBACK_FEATURES);
        setCommunityStats(buildCommunityStats(FALLBACK_FEATURES));
      }
    };

    fetchFeatures();
  }, [sortBy, sortOrder, statusFilter]);
  
  // Handle voting
  const handleVote = async (featureId) => {
    try {
      // Call the API to register the vote
      const response = await fetch(`https://app.getsortio.com/api/feature-leaderboard/${featureId}/vote/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      const apiResponse = await response.json();
      
      // Process the API response based on its format
      let updatedFeature;
      
      if (apiResponse.id) {
        // Direct feature object returned
        updatedFeature = {
          ...apiResponse,
          name: apiResponse.title || apiResponse.name, // Handle both title and name fields
          normalized_score: apiResponse.normalized_score || 0
        };
      } else if (apiResponse.feature) {
        // Feature nested in response object
        const feature = apiResponse.feature;
        updatedFeature = {
          ...feature,
          name: feature.title || feature.name,
          normalized_score: feature.normalized_score || 0
        };
      } else {
        // If we can't determine the format, just increment the score locally
        updatedFeature = null;
      }
      
      // Update the features list with the response data
      setFeatures(prevFeatures => 
        prevFeatures.map(feature => {
          if (feature.id === featureId) {
            if (updatedFeature) {
              return updatedFeature;
            } else {
              // Local fallback - just increment the score slightly
              return {
                ...feature,
                normalized_score: Math.min(1, (feature.normalized_score || 0) + 0.01)
              };
            }
          }
          return feature;
        })
      );
      
      // Update voted features in state and localStorage
      const updatedVotes = [...votedFeatures, featureId];
      setVotedFeatures(updatedVotes);
      localStorage.setItem('voted_features', JSON.stringify(updatedVotes));
      
      // Update stats
      setCommunityStats(prev => ({
        ...prev,
        totalVotes: prev.totalVotes + 1
      }));
    } catch (error) {
      console.error('Error voting for feature:', error);
      
      // Fallback to client-side update if API call fails
      setFeatures(prevFeatures => 
        prevFeatures.map(feature => 
          feature.id === featureId 
            ? { 
                ...feature, 
                // Increase normalized_score slightly (simulated)
                normalized_score: Math.min(1, (feature.normalized_score || 0) + 0.01)
              } 
            : feature
        )
      );
      
      // Update voted features in state and localStorage
      const updatedVotes = [...votedFeatures, featureId];
      setVotedFeatures(updatedVotes);
      localStorage.setItem('voted_features', JSON.stringify(updatedVotes));
      
      // Update stats
      setCommunityStats(prev => ({
        ...prev,
        totalVotes: prev.totalVotes + 1
      }));
    }
  };
  
  // Handle feature suggestion
  const handleFeatureSuggestion = async (suggestion) => {
    try {
      // Call the API to create a new feature request
      const response = await fetch('https://app.getsortio.com/api/feature-leaderboard/create/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: suggestion.title, 
          description: suggestion.description,
          category: suggestion.category
        })
      });
      
      if (!response.ok) {
        // Check if this is a 400 error for inappropriate content
        if (response.status === 400) {
          const errorData = await response.json();
          if (errorData.error && errorData.error.includes("flagged as inappropriate")) {
            // Show error message for inappropriate content
            alert('Your feature request was flagged as inappropriate or unrelated to file sorting features. Please submit a feature relevant to file organization.');
            return;
          }
        }
        throw new Error(`API error: ${response.status}`);
      }
      
      const apiResponse = await response.json();
      
      // Handle different API response formats
      let createdFeature;
      
      if (apiResponse.id) {
        // Direct feature object returned
        createdFeature = {
          id: apiResponse.id,
          name: apiResponse.title || apiResponse.name, // Handle both title and name fields
          description: apiResponse.description,
          normalized_score: apiResponse.normalized_score || 0,
          tier: apiResponse.tier || "consideration",
          status: apiResponse.status || "proposed",
          is_trending: apiResponse.is_trending || false,
          isNew: true
        };
      } else if (apiResponse.feature) {
        // Feature nested in response object
        const feature = apiResponse.feature;
        createdFeature = {
          id: feature.id,
          name: feature.title || feature.name,
          description: feature.description,
          normalized_score: feature.normalized_score || 0,
          tier: feature.tier || "consideration",
          status: feature.status || "proposed",
          is_trending: feature.is_trending || false,
          isNew: true
        };
      } else {
        // Fallback
        createdFeature = {
          id: `new-${Date.now()}`,
          name: suggestion.title,
          description: suggestion.description,
          normalized_score: 0.01,
          status: "proposed",
          tier: "consideration",
          is_trending: true,
          isNew: true
        };
      }
      
      // Add the newly created feature to the list
      setFeatures(prev => [createdFeature, ...prev]);
      
      // Automatically vote for the new feature
      const updatedVotes = [...votedFeatures, createdFeature.id];
      setVotedFeatures(updatedVotes);
      localStorage.setItem('voted_features', JSON.stringify(updatedVotes));
      
      // Update stats
      setCommunityStats(prev => ({
        ...prev,
        totalVotes: prev.totalVotes + 1,
        pendingFeatures: prev.pendingFeatures + 1
      }));
      
      // Show success message
      alert('Your feature suggestion has been submitted! Thank you for contributing to Sortio.');
    } catch (error) {
      console.error('Error submitting feature suggestion:', error);
      
      // Check if we're in development mode (using mock data)
      if (process.env.NODE_ENV === 'development' || features.length <= 20) { // Assuming if features.length is small, we're using mock data
        // Fallback to client-side mock if API fails
        const newFeature = {
          id: `new-${Date.now()}`,
          name: suggestion.title,
          description: suggestion.description,
          normalized_score: 0.01, // Start with a small score
          status: "proposed",
          tier: "consideration", // Default tier for new suggestions
          is_trending: true, // Mark as trending since it's new
          isNew: true
        };
        
        setFeatures(prev => [newFeature, ...prev]);
        
        // Automatically vote for the new feature
        const updatedVotes = [...votedFeatures, newFeature.id];
        setVotedFeatures(updatedVotes);
        localStorage.setItem('voted_features', JSON.stringify(updatedVotes));
        
        // Update stats
        setCommunityStats(prev => ({
          ...prev,
          totalVotes: prev.totalVotes + 1,
          pendingFeatures: prev.pendingFeatures + 1
        }));
        
        // Show success message
        alert('Your feature suggestion has been submitted! Thank you for contributing to Sortio.');
      } else {
        // Show generic error in production
        alert('Sorry, there was an issue submitting your feature request. Please try again later.');
      }
    }
  };
  
  // Handle email subscription
  const handleSubscribe = (subscription) => {
    // In a real app, you would call an API here
    console.log('Subscription:', subscription);
    
    // Show success message
    alert(`Thank you for subscribing! We'll keep you updated on ${subscription.subscribeAll ? 'all feature updates' : 'features you voted for'}.`);
  };
  
  // Refresh the feature scores periodically
  useEffect(() => {
    const refreshScores = async () => {
      try {
        const response = await fetch('https://app.getsortio.com/api/feature-leaderboard/refresh-scores/', {
          method: 'POST'
        });
        
        if (response.ok) {
          const updatedFeatures = await response.json();
          setFeatures(updatedFeatures);
        }
      } catch (error) {
        console.error('Error refreshing scores:', error);
      }
    };
    
    // Refresh scores every 5 minutes
    const intervalId = setInterval(refreshScores, 5 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);
  
  // Get feature detail by ID
  const getFeatureDetail = async (featureId) => {
    try {
      const response = await fetch(`https://app.getsortio.com/api/feature-leaderboard/${featureId}/`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching feature detail:', error);
      // Fallback to local state if API fails
      return features.find(f => f.id === featureId);
    }
  };
  
  // Filter and sort features
  const filteredAndSortedFeatures = features
    .filter(feature => {
      // Filter by search term
      const matchesSearch = searchTerm === '' || 
        feature.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
        feature.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Filter by status - handle status with underscores (in_progress)
      const matchesStatus = statusFilter === 'all' || feature.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      // Sort by selected field
      let comparison = 0;
      
      switch (sortBy) {
        case 'votes':
          // Use normalized_score for popularity-based sorting
          comparison = (a.normalized_score || 0) - (b.normalized_score || 0);
          break;
        case 'trending':
          // Sort trending first
          if (a.is_trending && !b.is_trending) comparison = -1;
          if (!a.is_trending && b.is_trending) comparison = 1;
          // For items with same trending status, sort by normalized_score
          if (comparison === 0) comparison = (a.normalized_score || 0) - (b.normalized_score || 0);
          break;
        case 'newest':
          // In a real app, would compare creation dates
          // This is a placeholder since we don't have real creation dates in mock data
          comparison = (b.id || '').localeCompare(a.id || '');
          break;
        case 'updated':
          // In a real app, would compare last updated dates
          // This is a placeholder
          comparison = 0;
          break;
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'status':
          const statusOrder = {
            'completed': 3, 
            'in_progress': 2, 
            'planned': 1, 
            'proposed': 0
          };
          comparison = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0);
          break;
        default:
          comparison = 0;
      }
      
      // Apply sort order
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <div className="min-h-screen bg-black pt-24 pb-16">
      {/* Background blur orbs */}
      <div className="fixed -bottom-32 -right-32 w-96 h-96 bg-sortio-primary/10 rounded-full blur-3xl"></div>
      <div className="fixed -top-32 -left-32 w-96 h-96 bg-sortio-secondary/10 rounded-full blur-3xl"></div>
      
      <Helmet>
        <title>Feature Leaderboard - Sortio</title>
        <meta name="description" content="Vote on and suggest new features for Sortio. Help shape the future of intelligent file organization." />
        <link rel="canonical" href="https://www.getsortio.com/leaderboard" />
        <meta property="og:title" content="Feature Leaderboard - Sortio" />
        <meta property="og:description" content="Vote on and suggest new features for Sortio. Help shape the future of intelligent file organization." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.getsortio.com/leaderboard" />
      </Helmet>
      
      <div className="container mx-auto px-4 sm:px-6 relative z-10">
        {/* Page header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-apple-50 to-sortio-primary">Feature Leaderboard</span>
          </h1>
          <p className="text-apple-300 text-lg max-w-2xl mx-auto">
            Help shape the future of Sortio by voting on features and suggesting new ideas. 
            Your input directly influences our development roadmap.
          </p>
        </div>
        
        {/* Community stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
          <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-5 rounded-2xl text-center relative overflow-hidden group hover:border-sortio-primary/30 transition-colors duration-300">
            <div className="absolute inset-0 bg-sortio-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-sortio-primary/10 flex items-center justify-center">
                <Heart className="text-sortio-primary" size={24} />
              </div>
              <div className="text-3xl font-bold text-apple-50 mb-1">
                {communityStats.totalVotes.toLocaleString()}
              </div>
              <div className="text-sm text-apple-400">Total Votes</div>
            </div>
          </div>
          
          <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-5 rounded-2xl text-center relative overflow-hidden group hover:border-sortio-secondary/30 transition-colors duration-300">
            <div className="absolute inset-0 bg-sortio-secondary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-sortio-secondary/10 flex items-center justify-center">
                <Check className="text-sortio-secondary" size={24} />
              </div>
              <div className="text-3xl font-bold text-apple-50 mb-1">
                {communityStats.featuresImplemented}
              </div>
              <div className="text-sm text-apple-400">Features Implemented</div>
            </div>
          </div>
          
          <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-5 rounded-2xl text-center relative overflow-hidden group hover:border-sortio-accent/30 transition-colors duration-300">
            <div className="absolute inset-0 bg-sortio-accent/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-sortio-accent/10 flex items-center justify-center">
                <Zap className="text-sortio-accent" size={24} />
              </div>
              <div className="text-3xl font-bold text-apple-50 mb-1">
                {communityStats.pendingFeatures}
              </div>
              <div className="text-sm text-apple-400">Features Pending</div>
            </div>
          </div>
          
          <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-5 rounded-2xl text-center relative overflow-hidden group hover:border-blue-400/30 transition-colors duration-300">
            <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative z-10">
              <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-500/10 flex items-center justify-center">
                <Users className="text-blue-400" size={24} />
              </div>
              <div className="text-3xl font-bold text-apple-50 mb-1">
                {communityStats.totalCommunityMembers.toLocaleString()}+
              </div>
              <div className="text-sm text-apple-400">Community Members</div>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          {/* Main column - Feature list */}
          <div className="md:col-span-2">
            {/* Controls */}
            <div className="backdrop-blur-md bg-apple-700/30 border border-apple-600/20 p-5 rounded-2xl mb-8 relative overflow-hidden">
              {/* Subtle blur effect */}
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-apple-700/30 to-transparent opacity-10"></div>
              
              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* Search */}
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-apple-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-11 pr-4 py-3 border border-apple-700/70 rounded-xl bg-apple-800/50 text-apple-100 placeholder-apple-500 focus:outline-none focus:ring-1 focus:ring-sortio-primary/70 focus:border-sortio-primary/30 transition-all duration-200"
                      placeholder="Search features..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  
                  {/* Status filter */}
                  <div className="relative min-w-[180px]">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Filter className="h-5 w-5 text-apple-400" />
                    </div>
                    <select 
                      className="block w-full appearance-none pl-11 pr-10 py-3 border border-apple-700/70 rounded-xl bg-apple-800/50 text-apple-100 focus:outline-none focus:ring-1 focus:ring-sortio-primary/70 focus:border-sortio-primary/30 transition-all duration-200"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Statuses</option>
                      <option value="proposed">Proposed</option>
                      <option value="planned">Planned</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
                      <ChevronDown className="h-5 w-5 text-apple-400" />
                    </div>
                  </div>
                </div>
                
                {/* Sort options */}
                <div className="flex flex-wrap items-center gap-4 mt-5 border-t border-apple-700/20 pt-4">
                  <div className="text-sm text-apple-300 font-medium">Sort by:</div>
                  <div className="flex flex-wrap gap-2">
                    {/* Popularity sort */}
                    <button 
                      onClick={() => {
                        if (sortBy === 'votes') {
                          setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('votes');
                          setSortOrder('desc');
                        }
                      }}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center transition-all duration-200 ${
                        sortBy === 'votes' 
                          ? 'bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30' 
                          : 'bg-apple-800/50 text-apple-300 border border-apple-700/50 hover:bg-apple-800 hover:border-apple-700'
                      }`}
                    >
                      Popularity
                      {sortBy === 'votes' && (
                        sortOrder === 'desc' ? 
                          <ArrowDown size={14} className="ml-1.5" /> : 
                          <ArrowUp size={14} className="ml-1.5" />
                      )}
                    </button>
                    
                    {/* Trending sort */}
                    <button 
                      onClick={() => {
                        setSortBy('trending');
                        setSortOrder('desc');
                      }}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center transition-all duration-200 ${
                        sortBy === 'trending' 
                          ? 'bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30' 
                          : 'bg-apple-800/50 text-apple-300 border border-apple-700/50 hover:bg-apple-800 hover:border-apple-700'
                      }`}
                    >
                      Trending
                      {sortBy === 'trending' && <ArrowDown size={14} className="ml-1.5" />}
                    </button>
                    
                    {/* Newest sort */}
                    <button 
                      onClick={() => {
                        setSortBy('newest');
                        setSortOrder('desc');
                      }}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center transition-all duration-200 ${
                        sortBy === 'newest' 
                          ? 'bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30' 
                          : 'bg-apple-800/50 text-apple-300 border border-apple-700/50 hover:bg-apple-800 hover:border-apple-700'
                      }`}
                    >
                      Newest
                      {sortBy === 'newest' && <ArrowDown size={14} className="ml-1.5" />}
                    </button>
                    
                    {/* Recently Updated sort */}
                    <button 
                      onClick={() => {
                        setSortBy('updated');
                        setSortOrder('desc');
                      }}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center transition-all duration-200 ${
                        sortBy === 'updated' 
                          ? 'bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30' 
                          : 'bg-apple-800/50 text-apple-300 border border-apple-700/50 hover:bg-apple-800 hover:border-apple-700'
                      }`}
                    >
                      Updated
                      {sortBy === 'updated' && <ArrowDown size={14} className="ml-1.5" />}
                    </button>
                    
                    {/* Name sort */}
                    <button 
                      onClick={() => {
                        if (sortBy === 'name') {
                          setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                        } else {
                          setSortBy('name');
                          setSortOrder('asc');
                        }
                      }}
                      className={`px-4 py-1.5 rounded-full text-sm flex items-center transition-all duration-200 ${
                        sortBy === 'name' 
                          ? 'bg-sortio-primary/20 text-sortio-primary border border-sortio-primary/30' 
                          : 'bg-apple-800/50 text-apple-300 border border-apple-700/50 hover:bg-apple-800 hover:border-apple-700'
                      }`}
                    >
                      Name
                      {sortBy === 'name' && (
                        sortOrder === 'desc' ? 
                          <ArrowDown size={14} className="ml-1.5" /> : 
                          <ArrowUp size={14} className="ml-1.5" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Features list */}
            {isLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-sortio-primary"></div>
              </div>
            ) : error ? (
              <div className="apple-panel p-8 text-center border-red-500/20">
                <p className="text-red-400">{error}</p>
              </div>
            ) : (
              <div>
                {statusFilter === 'completed' && (
                  <h2 className="text-2xl font-medium text-apple-50 mb-4 flex items-center">
                    <Check size={20} className="text-green-500 mr-2" />
                    Implemented Features
                  </h2>
                )}
                
                {statusFilter === 'in_progress' && (
                  <h2 className="text-2xl font-medium text-apple-50 mb-4 flex items-center">
                    <Zap size={20} className="text-amber-500 mr-2" />
                    In Development
                  </h2>
                )}
                
                {filteredAndSortedFeatures.length === 0 ? (
                  <div className="apple-panel p-8 text-center">
                    <p className="text-apple-300">No features matching your criteria</p>
                  </div>
                ) : (
                  filteredAndSortedFeatures
                    .filter(feature => statusFilter === 'completed' || feature.status !== 'completed')
                    .map(feature => (
                      <FeatureRequestCard 
                        key={feature.id} 
                        feature={feature} 
                        onVote={handleVote}
                        hasVoted={votedFeatures.includes(feature.id)}
                      />
                    ))
                )}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Suggest a feature */}
            <SuggestFeatureForm onSubmit={handleFeatureSuggestion} />
            
            {/* Email subscription */}
            <EmailCaptureForm onSubscribe={handleSubscribe} votedFeatureIds={votedFeatures} />
          </div>
        </div>
        
        {/* Recently implemented */}
        <div className="mt-16">
          <h2 className="text-2xl font-medium text-apple-50 mb-6 flex items-center">
            <Check size={20} className="text-green-500 mr-2" />
            Recently Implemented
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {features
              .filter(feature => feature.status === 'completed')
              .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0))
              .slice(0, 3)
              .map(feature => (
                <div key={feature.id} className="apple-panel p-5 rounded-xl backdrop-blur-sm">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-medium text-apple-50">{feature.name}</h3>
                    <div className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/30">
                      <Check size={12} className="mr-1" />
                      Implemented
                    </div>
                  </div>
                  <p className="text-sm text-apple-300 mb-4">{feature.description}</p>
                  <a 
                    href="/download" 
                    className="flex items-center text-sm text-sortio-primary hover:underline"
                  >
                    <Download size={14} className="mr-1" />
                    Try it now
                  </a>
                </div>
              ))
            }
          </div>
        </div>
        
        {/* Download CTA */}
        <div className="mt-16 backdrop-blur-md relative overflow-hidden rounded-2xl max-w-3xl mx-auto">
          {/* Glass morphism background with gradient */}
          <div className="absolute inset-0 bg-gradient-to-br from-sortio-primary/20 via-apple-700/40 to-sortio-secondary/20 z-0"></div>
          
          {/* Content */}
          <div className="relative z-10 p-7 border border-white/10 rounded-2xl text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-white/10 backdrop-blur-xl flex items-center justify-center mx-auto">
              <Download className="text-white" size={28} />
            </div>
            
            <h3 className="text-2xl font-semibold text-white mb-3">Try Sortio Today</h3>
            <p className="text-gray-300 text-base mb-6 leading-relaxed max-w-xl mx-auto">
              Experience intelligent file organization with our growing list of community-driven features.
            </p>
            
            <a 
              href="/download" 
              className="group inline-block px-8 py-3.5 rounded-full flex justify-center items-center bg-white text-black font-medium hover:bg-opacity-90 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              <span className="relative z-10 flex items-center">
                Download Now 
                <ArrowRight className="ml-2 group-hover:translate-x-0.5 transition-transform duration-300" size={18} />
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaderboardPage;